/**
 * Aterrissagem de âncora (03b): ao montar/receber hash, rola até a seção e
 * aplica a classe `hub-anchor-pulse` — pulsa 2× e some (animação CSS, não JS).
 * ⚠️ scrollIntoView em container oculto é INERTE (lição medida
 * reference_aba_oculta_inerte_intersection_smooth) — chamar após raf.
 *
 * Fix round 1 (Finding 3, achado do coordenador): a versão original só
 * cancelava o `requestAnimationFrame` pendente no cleanup. Se o componente
 * desmontasse DEPOIS do frame disparar mas ANTES do `setTimeout` de 1600ms, o
 * timeout continuava vivo tocando um elemento já fora da árvore montada, e o
 * listener `animationend` ficava pendurado. `limpar` fecha as 3 fases —
 * raf pendente, timeout pendente, listener pendente — não importa em qual
 * delas o desmonte acontece.
 */
import { useEffect } from 'react';

export function useAnchorLanding(hash: string): void {
  useEffect(() => {
    if (hash.length < 2) return;
    const found = document.getElementById(hash.slice(1));
    if (found === null) return;
    // Referência não-nula estável — evita que TS perca a narrowing de `found`
    // dentro da `function` aninhada (declaração hoisted, closure conservadora).
    const el: HTMLElement = found;

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    function limpar(): void {
      el.classList.remove('hub-anchor-pulse');
      if (timeoutId !== null) clearTimeout(timeoutId);
      el.removeEventListener('animationend', limpar);
    }

    const raf = requestAnimationFrame(() => {
      el.scrollIntoView({ block: 'start' });
      el.classList.add('hub-anchor-pulse');
      timeoutId = setTimeout(limpar, 1600);
      el.addEventListener('animationend', limpar, { once: true });
    });

    return () => {
      cancelAnimationFrame(raf);
      limpar();
    };
  }, [hash]);
}
